/**
 * TwilioInboundWebhookCard · v2.7.65+E169
 * ─────────────────────────────────────────────────────────────────────────
 * Admin → Payments → Twilio · Inbound STOP Webhook info card.
 *
 * Shows the fully-qualified webhook URL for `/api/twilio/sms/inbound` so
 * the admin can paste it into Twilio Console → Phone Numbers → their
 * number → Messaging · "A MESSAGE COMES IN" → HTTP POST.
 *
 * Includes:
 *   · Copy-to-clipboard button
 *   · Recent inbound log (STOP / START / HELP / unmatched) pulled from
 *     `GET /api/admin/sms/inbound` so the admin can prove opt-outs are landing.
 *   · Reminder line about the STOP → sms_consent=false auto-flip.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  MessageSquare, Copy, CheckCircle2, ShieldOff, ShieldCheck,
  HelpCircle, MessageCircle, RefreshCw, ExternalLink,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

const ACTION_STYLES = {
  opt_out:   { Icon: ShieldOff,     label: "STOP",      tint: "border-rose-500/40 text-rose-300 bg-rose-500/5" },
  opt_in:    { Icon: ShieldCheck,   label: "START",     tint: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
  help:      { Icon: HelpCircle,    label: "HELP",      tint: "border-sky-500/40 text-sky-300 bg-sky-500/5" },
  unmatched: { Icon: MessageCircle, label: "Unmatched", tint: "border-zinc-600 text-zinc-300 bg-zinc-800/50" },
};

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function TwilioInboundWebhookCard() {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = `${backend.replace(/\/$/, "")}/api/twilio/sms/inbound`;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/sms/inbound?limit=20");
      setItems(data?.items || []);
    } catch (err) {
      console.warn("sms inbound log fetch failed", err?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      toast.success("Webhook URL copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="twilio-inbound-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-bold flex items-center gap-2 text-zinc-100">
            <MessageSquare className="h-4 w-4 text-emerald-300"/>Twilio · Inbound STOP Webhook
          </h3>
          <p className="text-[11px] text-zinc-500 mt-1 max-w-2xl leading-relaxed">
            Paste this URL into Twilio Console → Phone Numbers → <span className="text-zinc-300 font-semibold">your number</span> →
            Messaging · &quot;A MESSAGE COMES IN&quot; · HTTP POST. When a subscriber replies
            <span className="text-rose-300 font-bold"> STOP </span>
            (or UNSUBSCRIBE / CANCEL / QUIT / END), Buller Taxi automatically flips their
            <code className="mx-1 px-1.5 py-0.5 rounded bg-zinc-800 text-emerald-200 text-[10px]">sms_consent → false</code>
            on every matching rider + driver row. Replying
            <span className="text-emerald-300 font-bold"> START </span>
            re-opts them in.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="twilio-inbound-refresh"
          className="border-zinc-700 bg-zinc-950 h-8 shrink-0"
        >
          <RefreshCw className={`h-3 w-3 mr-1.5 ${loading ? "animate-spin" : ""}`}/>Refresh
        </Button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 mb-4" data-testid="twilio-inbound-url-block">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Webhook URL</div>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 min-w-0 truncate font-mono text-xs text-emerald-200 bg-zinc-900/50 px-2 py-1.5 rounded border border-zinc-800"
            data-testid="twilio-inbound-url"
          >
            {webhookUrl}
          </code>
          <Button
            size="sm"
            onClick={copyUrl}
            data-testid="twilio-inbound-copy"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-widest text-xs h-8 shrink-0"
          >
            {copied
              ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5"/>Copied</>
              : <><Copy className="h-3.5 w-3.5 mr-1.5"/>Copy</>}
          </Button>
          <a
            href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="twilio-inbound-open-console"
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-amber-300 px-2 py-1.5 border border-zinc-700 rounded"
          >
            <ExternalLink className="h-3 w-3"/>Twilio
          </a>
        </div>
        <div className="text-[10px] text-zinc-500 mt-2">
          Method: <span className="text-zinc-300 font-mono">POST</span> · Content-Type:
          <span className="text-zinc-300 font-mono ml-1">application/x-www-form-urlencoded</span>
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid="twilio-inbound-log">
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Recent Inbound Log</div>
          <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[10px]" data-testid="twilio-inbound-count">
            {items.length} entries
          </Badge>
        </div>
        <div className="max-h-[240px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-xs" data-testid="twilio-inbound-empty">
              No inbound SMS logged yet. Once Twilio starts routing to the webhook above, entries will appear here.
            </div>
          ) : (
            <table className="w-full text-xs">
              <tbody className="divide-y divide-zinc-800">
                {items.map((it) => {
                  const style = ACTION_STYLES[it.action] || ACTION_STYLES.unmatched;
                  const Icon = style.Icon;
                  return (
                    <tr key={it.id} data-testid={`twilio-inbound-row-${it.id}`} className="hover:bg-zinc-900/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${style.tint}`}>
                          <Icon className="h-3 w-3"/>{style.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-300 whitespace-nowrap">{it.from || "—"}</td>
                      <td className="px-3 py-2 text-zinc-200 truncate max-w-[220px]" title={it.body}>{it.body || "—"}</td>
                      <td className="px-3 py-2 text-zinc-500 whitespace-nowrap text-[10px]">
                        {it.rows_touched
                          ? `↻ riders:${it.rows_touched.customers || 0} drivers:${it.rows_touched.drivers || 0}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-zinc-500 whitespace-nowrap text-[10px]">{fmtTs(it.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
        <span className="text-amber-300 font-bold">Compliance</span> · STOP replies are honored immediately.
        Non-transactional outbound SMS is refused by the send-filter guard when the destination row is not
        <span className="text-emerald-300 font-semibold"> opted-in</span>. Transactional messages
        (ride confirmations, driver-arrival, panic) always send.
      </p>
    </Card>
  );
}
